import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import CustomModal from '../../CustomModal'
import StyledModal from '../../styledModals/StyledModal'
import Text from '../../../Text'
import Switch from '../../../Switch'
import Select from '../../../select/Select'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import { useAppStore } from '../../../../stores/application/useAppStore'
import { useInboxStore } from '../../../../stores/inbox/useInboxStore'
import { useInboxHandlers } from '../../../../services/actions/inbox/useInboxHandlers'
import { useExtensionRequest } from '../../../../services/apis/extension/useExtensionRequest'
import {
    createHubspotMessagePreview,
    HubspotConversationExportModalPayload,
    HubspotConversationExportType,
} from '../../../../pages/canvas/components/inbox/hubspotInboxExportUtils'

interface IHubSpotConversationExportModalProps {
    payload: HubspotConversationExportModalPayload
    onClose: () => void
}

const normalizeInboxesResponse = (raw: any) => {
    if (!raw || raw instanceof Error) return { inboxes: [] as any[], selectedInboxId: undefined as string | undefined }
    const data = raw?.result ?? raw
    if (Array.isArray(data)) return { inboxes: data, selectedInboxId: undefined }
    return {
        inboxes: data?.inboxes ?? [],
        selectedInboxId: data?.selectedInboxId,
    }
}

const HubSpotConversationExportModal: FunctionComponent<IHubSpotConversationExportModalProps> = ({ payload, onClose }) => {
    const {
        bulk = false,
        isSales = false,
        conversationId,
        userEmail,
        linkedinUrl,
        connectionId,
        participants = [],
        items = [],
        messages: payloadMessages = [],
    } = payload

    const { showSuccessToast, showErrorToast } = useAppHandlers()
    const { store: { extensionInstalled } } = useAppStore()
    const {
        store: {
            messages: storeMessages,
            fetchingMessages,
            fetchingMoreMessages,
            allMessagesFetched,
            selectedConversation,
        },
    } = useInboxStore()
    const { loadMoreMessages, getMessagesFirstPage } = useInboxHandlers()
    const { queueInboxHubspotExport, getHubspotInboxesFromExtension, getHubspotOAuthStatusFromExtension } =
        useExtensionRequest()

    const [selectedOption, setSelectedOption] = useState<HubspotConversationExportType>('activity')
    const [toggleState, setToggleState] = useState(true)
    const [continuousToggle, setContinuousToggle] = useState(true)
    const [selectAllToggle, setSelectAllToggle] = useState(true)
    const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set())
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [hasOptionalScopes, setHasOptionalScopes] = useState<boolean | null>(null)
    const [checkingScopes, setCheckingScopes] = useState(true)

    const [hubspotInboxes, setHubspotInboxes] = useState<any[]>([])
    const [selectedInbox, setSelectedInbox] = useState<string>('')
    const [loadingInboxes, setLoadingInboxes] = useState(false)
    const [inboxAlreadySaved, setInboxAlreadySaved] = useState(false)

    const selectionRef = useRef<HTMLDivElement>(null)

    // Same as extension: prefer live store, fall back to snapshot passed when opening the modal.
    const messages = storeMessages.length > 0 ? storeMessages : payloadMessages

    useEffect(() => {
        if (bulk || messages.length > 0) return
        const selectedId = selectedConversation?.conversationId || selectedConversation?._id
        if (!selectedId || !conversationId || selectedId !== conversationId) return
        if (fetchingMessages) return
        getMessagesFirstPage(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bulk, conversationId, selectedConversation?.conversationId, messages.length, fetchingMessages])

    const messageOptions = useMemo(() => {
        const mapped = messages.map((message: any, index: number) => {
            const key = message.messageId || `${message.dateTimestamp || 'msg'}-${index}`
            return {
                id: key,
                text: createHubspotMessagePreview(message),
                timestamp: message.dateTimestamp,
                isSent: message.isSent,
            }
        })
        return mapped.sort((a: { timestamp?: number | string }, b: { timestamp?: number | string }) => {
            const aTime = typeof a.timestamp === 'number' ? a.timestamp : a.timestamp ? new Date(a.timestamp).getTime() : 0
            const bTime = typeof b.timestamp === 'number' ? b.timestamp : b.timestamp ? new Date(b.timestamp).getTime() : 0
            return bTime - aTime
        })
    }, [messages])

    const allMessageIds = useMemo(() => messageOptions.map((o: { id: string }) => o.id), [messageOptions])

    useEffect(() => {
        setCheckingScopes(true)
        getHubspotOAuthStatusFromExtension()
            .then(response => {
                const status = (response as any)?.result ?? response ?? {}
                const hasScopes = Boolean(status?.hasOptionalScopes)
                setHasOptionalScopes(hasScopes)
                if (selectedOption === 'inbox' && !hasScopes) {
                    setSelectedOption('activity')
                }
            })
            .catch(() => {
                setHasOptionalScopes(false)
                if (selectedOption === 'inbox') setSelectedOption('activity')
            })
            .finally(() => setCheckingScopes(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (selectedOption !== 'inbox' || hubspotInboxes.length > 0 || inboxAlreadySaved) return
        setLoadingInboxes(true)
        getHubspotInboxesFromExtension()
            .then(response => {
                const { inboxes, selectedInboxId } = normalizeInboxesResponse(response)
                setHubspotInboxes(inboxes)
                if (selectedInboxId) {
                    setSelectedInbox(selectedInboxId)
                    setInboxAlreadySaved(true)
                } else if (inboxes.length > 0) {
                    const first = inboxes[0]
                    setSelectedInbox(first.id || first.inboxId)
                }
            })
            .catch(err => console.error('Failed to fetch HubSpot inboxes:', err))
            .finally(() => setLoadingInboxes(false))
    }, [selectedOption, hubspotInboxes.length, inboxAlreadySaved, getHubspotInboxesFromExtension])

    const inboxSelectOptions = useMemo(
        () =>
            hubspotInboxes.map(inbox => ({
                value: inbox.id || inbox.inboxId,
                label: inbox.name || `Inbox ${inbox.id || inbox.inboxId}`,
            })),
        [hubspotInboxes]
    )

    const isMessageSelected = (id: string) => {
        if (toggleState) return true
        if (selectAllToggle) return !selectedMessages.has(id)
        return selectedMessages.has(id)
    }

    const handleToggleMessage = (id: string) => {
        if (toggleState) return
        setSelectedMessages(prev => {
            const next = new Set(prev)
            if (selectAllToggle) {
                if (next.has(id)) next.delete(id)
                else next.add(id)
            } else {
                if (next.has(id)) next.delete(id)
                else next.add(id)
            }
            return next
        })
    }

    const handleToggleState = (enabled: boolean) => {
        if (enabled) {
            setToggleState(true)
        } else {
            setToggleState(false)
            setSelectAllToggle(true)
            setSelectedMessages(new Set())
        }
    }

    const hasSelection = toggleState
        ? true
        : selectAllToggle
          ? selectedMessages.size < allMessageIds.length
          : selectedMessages.size > 0

    const canSubmitSingle =
        (selectedOption === 'activity' && Boolean(conversationId) && Boolean(connectionId || isSales) && !isSubmitting && hasSelection) ||
        (selectedOption === 'inbox' &&
            Boolean(conversationId) &&
            Boolean(connectionId || isSales) &&
            Boolean(selectedInbox) &&
            !isSubmitting &&
            hasSelection)

    const canSubmitBulk =
        bulk && items.length > 0 && !isSubmitting && (selectedOption === 'activity' || (selectedOption === 'inbox' && Boolean(selectedInbox)))

    const handleScroll = async (event: React.UIEvent<HTMLDivElement>) => {
        if (!selectionRef.current || fetchingMoreMessages || allMessagesFetched) return
        const target = event.currentTarget
        const reachedBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= 5
        if (reachedBottom) {
            await loadMoreMessages({ target: { scrollTop: 0 } })
        }
    }

    const handleProceed = async () => {
        if (!extensionInstalled) {
            showErrorToast({ message: 'Please install the LeadDelta extension to export conversations to HubSpot' })
            return
        }

        try {
            setIsSubmitting(true)
            const exportType = selectedOption

            if (bulk) {
                const validItems = items.filter(
                    item => (item.connectionId || item.isSales) && item.participants?.length === 1
                )
                if (!validItems.length) {
                    onClose()
                    return
                }
                const preparedItems = validItems.map(item => ({
                    ...item,
                    exportType,
                    inboxId: exportType === 'inbox' ? selectedInbox : undefined,
                    selection: { importAll: true, excludedIds: [], includedIds: [] },
                    continuous: continuousToggle,
                }))
                const result = await queueInboxHubspotExport(preparedItems, isSales)
                if (result === false || (result as any)?.error) {
                    showErrorToast({ message: 'Failed to queue HubSpot export' })
                    return
                }
                showSuccessToast({ message: 'Export to HubSpot was queued successfully' })
            } else {
                let singleImportAll = toggleState
                let singleExcludedIds: string[] = []
                let singleIncludedIds: string[] = []

                if (toggleState) {
                    singleImportAll = true
                } else if (selectAllToggle) {
                    singleImportAll = true
                    singleExcludedIds = Array.from(selectedMessages)
                } else {
                    singleImportAll = false
                    singleIncludedIds = Array.from(selectedMessages)
                }

                const queueItem = {
                    conversationId,
                    connectionId,
                    userEmail,
                    linkedinUrl,
                    isSales,
                    participants: [...participants],
                    exportType,
                    inboxId: exportType === 'inbox' ? selectedInbox : undefined,
                    continuous: continuousToggle,
                    selection: {
                        importAll: singleImportAll,
                        excludedIds: singleExcludedIds,
                        includedIds: singleIncludedIds,
                    },
                    loadedMessages: messages.map((message: any) => ({
                        messageId: message.messageId,
                        text: createHubspotMessagePreview(message),
                        timestamp: message.dateTimestamp,
                        isSent: message.isSent,
                    })),
                }
                const result = await queueInboxHubspotExport([queueItem], isSales)
                if (result === false || (result as any)?.error) {
                    showErrorToast({ message: 'Failed to queue HubSpot export' })
                    return
                }
                showSuccessToast({ message: 'Export to HubSpot was queued successfully' })
            }
            onClose()
        } catch (error) {
            console.error('HubSpot export failed:', error)
            showErrorToast({ message: 'Failed to queue HubSpot export' })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <StyledModal show onHide={onClose} width="680px" backdrop>
        <CustomModal
            headingText="Export LinkedIn Conversation Messages to HubSpot"
            descriptionText="Select how you want to log this conversation's messages."
            closeButton
            onCancelHandler={() => {
                onClose()
                return false
            }}
            bodyPadding="0 24px 24px"
            confirmButton={{
                text: isSubmitting ? 'Exporting...' : 'Proceed',
                disable: isSubmitting || (bulk ? !canSubmitBulk : !canSubmitSingle),
                onClickHandler: handleProceed,
            }}
            cancelButton={{
                text: 'Cancel',
                onClickHandler: onClose,
            }}
        >
            <Body>
                <OptionGrid>
                    <OptionCard $selected={selectedOption === 'activity'} onClick={() => setSelectedOption('activity')}>
                        <OptionTitle>Log to Contact Activity</OptionTitle>
                        <OptionDescription>Save these LinkedIn messages on the HubSpot contact timeline.</OptionDescription>
                    </OptionCard>
                    <OptionCard
                        $selected={selectedOption === 'inbox'}
                        $disabled={!hasOptionalScopes && !checkingScopes}
                        onClick={() => {
                            if (!hasOptionalScopes && !checkingScopes) return
                            setSelectedOption('inbox')
                        }}
                    >
                        <OptionTitle>Import to HubSpot Inbox</OptionTitle>
                        <OptionDescription>
                            {!hasOptionalScopes && !checkingScopes
                                ? 'Requires HubSpot permissions. Please request permissions in HubSpot settings.'
                                : 'Bring LinkedIn conversations into the HubSpot inbox.'}
                        </OptionDescription>
                    </OptionCard>
                </OptionGrid>

                {selectedOption === 'inbox' && !inboxAlreadySaved && (
                    <InboxSelector>
                        <Text $bold>Select HubSpot Inbox</Text>
                        {loadingInboxes ? (
                            <MutedText>Loading inboxes...</MutedText>
                        ) : hubspotInboxes.length > 0 ? (
                            <Select
                                value={
                                    inboxSelectOptions.find(o => o.value === selectedInbox) ??
                                    inboxSelectOptions[0] ??
                                    null
                                }
                                options={inboxSelectOptions}
                                onChange={(option: any) => setSelectedInbox(option?.value || '')}
                                placeholder="Select inbox"
                            />
                        ) : (
                            <MutedText>No inboxes available. Please create an inbox in HubSpot first.</MutedText>
                        )}
                    </InboxSelector>
                )}

                {bulk ? (
                    <>
                        <BulkNote>
                            <strong>Note:</strong> You are exporting all messages for the selected conversation(s). No
                            per-message selection will be applied.
                        </BulkNote>
                        <ToggleRow>
                            <Switch prefilledValue={continuousToggle} onClickHandler={setContinuousToggle} />
                            <Text>Automatically sync new messages to HubSpot</Text>
                        </ToggleRow>
                    </>
                ) : (
                    <>
                        <ToggleRow>
                            <Switch prefilledValue={toggleState} onClickHandler={handleToggleState} />
                            <Text>Import all messages from this conversation</Text>
                        </ToggleRow>
                        <MessageSelectionWrapper $disabled={toggleState}>
                            <MessageSelectionHeader>
                                <Text $bold>Message selection</Text>
                                <HeaderActions>
                                    <LinkBtn type="button" disabled={!messageOptions.length} onClick={() => { setSelectAllToggle(true); setSelectedMessages(new Set()) }}>
                                        Select All
                                    </LinkBtn>
                                    <LinkBtn type="button" disabled={!messageOptions.length} onClick={() => { setSelectAllToggle(false); setSelectedMessages(new Set()) }}>
                                        Deselect All
                                    </LinkBtn>
                                </HeaderActions>
                            </MessageSelectionHeader>
                            <MessagesList ref={selectionRef} onScroll={handleScroll}>
                                {messageOptions.map((option: { id: string; text: string; timestamp?: number | string; isSent?: boolean }) => (
                                    <MessageRow key={option.id}>
                                        <input
                                            type="checkbox"
                                            checked={isMessageSelected(option.id)}
                                            onChange={() => handleToggleMessage(option.id)}
                                            disabled={toggleState}
                                        />
                                        <MessageContent>
                                            <MessageDirection $sent={option.isSent}>
                                                {option.isSent ? 'Sent' : 'Received'}
                                            </MessageDirection>
                                            <MessageText>{option.text}</MessageText>
                                            {option.timestamp && (
                                                <MessageMeta>
                                                    {moment(option.timestamp).format('MMM D, YYYY · HH:mm')}
                                                </MessageMeta>
                                            )}
                                        </MessageContent>
                                    </MessageRow>
                                ))}
                                {!messageOptions.length && fetchingMessages && (
                                    <MutedText>Loading messages…</MutedText>
                                )}
                                {!messageOptions.length && !fetchingMessages && (
                                    <MutedText>Messages will appear here once loaded.</MutedText>
                                )}
                                {fetchingMoreMessages && <MutedText>Loading more messages…</MutedText>}
                            </MessagesList>
                        </MessageSelectionWrapper>
                        <ToggleRow>
                            <Switch prefilledValue={continuousToggle} onClickHandler={setContinuousToggle} />
                            <Text>Automatically sync new messages to HubSpot</Text>
                        </ToggleRow>
                    </>
                )}
            </Body>
        </CustomModal>
        </StyledModal>
    )
}

export default HubSpotConversationExportModal

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 640px;
`

const OptionGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 8px;

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`

const OptionCard = styled.button<{ $selected: boolean; $disabled?: boolean }>`
    text-align: left;
    padding: 16px;
    border-radius: 8px;
    border: 2px solid ${p => (p.$selected ? '#5E6AD2' : '#E0E0E0')};
    background: ${p => (p.$selected ? '#F0F1FF' : '#FFFFFF')};
    cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${p => (p.$disabled ? 0.65 : 1)};

    &:hover {
        border-color: #5e6ad2;
    }
`

const OptionTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: #333;
    margin-bottom: 4px;
`

const OptionDescription = styled.div`
    font-size: 12px;
    color: #666;
    line-height: 1.4;
`

const InboxSelector = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`

const MessageSelectionWrapper = styled.div<{ $disabled?: boolean }>`
    opacity: ${p => (p.$disabled ? 0.55 : 1)};
    pointer-events: ${p => (p.$disabled ? 'none' : 'auto')};
`

const MessageSelectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`

const HeaderActions = styled.div`
    display: flex;
    gap: 8px;
`

const LinkBtn = styled.button`
    background: none;
    border: none;
    color: #5964ff;
    font-size: 13px;
    cursor: pointer;
    padding: 0;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`

const MessagesList = styled.div`
    max-height: 220px;
    overflow-y: auto;
    border: 1px solid #e8ecf3;
    border-radius: 8px;
    padding: 8px;
`

const MessageRow = styled.label`
    display: flex;
    gap: 10px;
    padding: 8px 4px;
    border-bottom: 1px solid #f0f2f7;
    cursor: pointer;

    &:last-child {
        border-bottom: none;
    }
`

const MessageContent = styled.div`
    flex: 1;
    min-width: 0;
`

const MessageDirection = styled.span<{ $sent?: boolean }>`
    font-size: 11px;
    font-weight: 700;
    color: ${p => (p.$sent ? '#5964ff' : '#8c96ad')};
    display: block;
    margin-bottom: 2px;
`

const MessageText = styled.div`
    font-size: 13px;
    color: #333;
    word-break: break-word;
`

const MessageMeta = styled.div`
    font-size: 11px;
    color: #8c96ad;
    margin-top: 4px;
`

const MutedText = styled.div`
    font-size: 13px;
    color: #8c96ad;
    padding: 8px 0;
`

const BulkNote = styled.div`
    font-size: 14px;
    color: #333;
    line-height: 1.5;
`
